import * as THREE from 'three';
import {isSketchArcObject} from '../../mesh/guards/isSketchArcObject';
import {isSketchCircleObject} from '../../mesh/guards/isSketchCircleObject';
import {isSketchCollectionObject} from '../../mesh/guards/isSketchCollectionObject';
import {isSketchEllipseObject} from '../../mesh/guards/isSketchEllipseObject';
import {isSketchLineObject} from '../../mesh/guards/isSketchLineObject';
import {isSketchParabolaObject} from '../../mesh/guards/isSketchParabolaObject';
import {isSketchSlotObject} from '../../mesh/guards/isSketchSlotObject';
import {isSketchSplineObject} from '../../mesh/guards/isSketchSplineObject';
import {isSketchStyleSplineObject} from '../../mesh/guards/isSketchStyleSplineObject';
import type {SketchContour} from '../../mesh/meshProxy/SketchContour';
import type {CollectSource} from '../../mesh/sketch/ISketchObject';
import {SketchBezierCurveClosureKind, type SketchBezierCurveMetadata,} from '../../mesh/sketch/SketchSplineObject';
import {SketchClosureResolver} from '../../sketch/closure';
import {isFullProfileEllipseSegment} from './offsetProfileCurveSegment';

type SplineLikeSource = Extract<
  CollectSource,
  { getCurveMetadata: () => SketchBezierCurveMetadata }
>;


/**
 * 曲线段构建步骤。
 */
type ProfileCurveSegmentBuildStep = {
  /** 集合源对象。 */
  source: CollectSource;
  /** 是否按闭合遍历方向反向输出。 */
  reversed: boolean;
};


/**
 * 将 Three 向量序列化为普通三维点。
 *
 * @param vector Three 向量。
 * @returns 三维点。
 */
function toPoint3(vector: THREE.Vector3): [number, number, number] {
  return [vector.x, vector.y, vector.z];
}

/**
 * 读取对象参考面的法向与坐标轴。
 *
 * @param source 带参考面的草图对象。
 * @returns 参考面轴向。
 */
function resolveReferencePlaneAxes(source: {
  referencePlane: {
    normal: THREE.Vector3;
    xAxis: THREE.Vector3;
    yAxis: THREE.Vector3;
  };
}): {
  normal: [number, number, number];
  xAxis: [number, number, number];
  yAxis: [number, number, number];
} {
  return {
    normal: toPoint3(source.referencePlane.normal),
    xAxis: toPoint3(source.referencePlane.xAxis),
    yAxis: toPoint3(source.referencePlane.yAxis),
  };
}

/**
 * 从草图直线对象构建精确曲线段。
 *
 * @param source 草图直线对象。
 * @returns 直线曲线段。
 */
function buildLineSegment(source: CollectSource): ProfileCurveSegment | null {
  if (!isSketchLineObject(source)) {
    return null;
  }
  return {
    kind: 'line',
    start: toPoint3(source.startPoint.getPosition()),
    end: toPoint3(source.endPoint.getPosition()),
  };
}

/**
 * 从草图圆弧对象构建精确曲线段。
 *
 * @param source 草图圆弧对象。
 * @returns 圆弧曲线段。
 */
function buildArcSegment(source: CollectSource): ProfileCurveSegment | null {
  if (!isSketchArcObject(source)) {
    return null;
  }
  const definition = source.getArcDefinition();
  const axes = resolveReferencePlaneAxes(source);
  return {
    kind: 'arc',
    center: toPoint3(definition.centerWorld),
    radius: definition.radius,
    startAngle: definition.startAngle,
    endAngle: definition.endAngle,
    clockwise: definition.clockwise,
    normal: axes.normal,
    xAxis: axes.xAxis,
  };
}

/**
 * 从草图椭圆对象构建精确椭圆或椭圆弧段。
 *
 * @param source 草图集合源对象。
 * @returns 椭圆曲线段；非椭圆对象返回 null。
 */
function buildEllipseSegment(source: CollectSource): Extract<ProfileCurveSegment, { kind: 'ellipse' }> | null {
  if (!isSketchEllipseObject(source)) {
    return null;
  }
  const definition = source.getEllipseDefinition();
  const axes = resolveReferencePlaneAxes(source);
  return {
    kind: 'ellipse',
    center: toPoint3(definition.centerWorld),
    radiusX: definition.radiusX,
    radiusY: definition.radiusY,
    rotation: definition.rotation,
    startAngle: definition.startAngle,
    endAngle: definition.endAngle,
    clockwise: definition.clockwise,
    normal: axes.normal,
    xAxis: axes.xAxis,
    yAxis: axes.yAxis,
  };
}

/**
 * 从草图抛物线对象构建精确抛物线曲线段。
 *
 * 设计说明：抛物线在 Puppy CAD 内核 侧没有直接几何绑定，这里把定义解析为
 * 顶点/焦点/侧向轴/轴向/焦距，供 ProfileWireBuilder 精确转换为二次 Bézier。
 *
 * @param source 草图抛物线对象。
 * @returns 抛物线曲线段；非抛物线对象返回 null。
 */
function buildParabolaSegment(source: CollectSource): ProfileCurveSegment | null {
  if (!isSketchParabolaObject(source)) {
    return null;
  }
  const definition = source.getParabolaDefinition();
  const normal = source.referencePlane.normal.clone().normalize();
  // 焦点相对顶点的向量投影到草图面上，得到抛物线轴向与焦距。
  const axisVector = definition.focalWorld.clone().sub(definition.apexWorld);
  axisVector.sub(normal.clone().multiplyScalar(axisVector.dot(normal)));
  const focalLength = axisVector.length();
  if (!Number.isFinite(focalLength) || focalLength <= 1e-12) {
    return null;
  }
  const axisDir = axisVector.normalize();
  const sideDir = new THREE.Vector3().crossVectors(axisDir, normal).normalize();
  return {
    kind: 'parabola',
    apex: toPoint3(definition.apexWorld),
    focal: toPoint3(definition.focalWorld),
    axisDir: toPoint3(axisDir),
    sideDir: toPoint3(sideDir),
    focalLength,
    startParameter: definition.startParameter,
    endParameter: definition.endParameter,
    curvePoints: source.getCurvePoints().map(toPoint3),
  };
}

/**
 * 从样条对象构建受控样条曲线段。
 *
 * @param source 草图样条对象。
 * @returns 样条曲线段。
 */
function buildSplineSegment(source: CollectSource): ProfileCurveSegment | null {
  if (!isSketchSplineObject(source) && !isSketchStyleSplineObject(source)) {
    return null;
  }
  const controlPoints = source.getControlPoints().map((point) => toPoint3(point.getPosition()));
  const curvePoints = source.getCurvePoints().map(toPoint3);
  const curveMetadata = (source as SplineLikeSource).getCurveMetadata();
  if (controlPoints.length < 2 || curvePoints.length < 2) {
    return null;
  }
  return {
    kind: 'spline',
    controlPoints,
    curvePoints,
    curveKind: curveMetadata.curveKind,
    degree: curveMetadata.degree,
    weights: [...curveMetadata.weights],
    isRational: curveMetadata.isRational,
    closureKind: curveMetadata.closureKind,
    isClosed: curveMetadata.closureKind === SketchBezierCurveClosureKind.Closed
      || source.isClosedGeometry,
  };
}

/**
 * 从集合源对象构建精确曲线段。
 *
 * @param source 集合源对象。
 * @param reversed 是否按闭合遍历方向反向输出。
 * @returns 曲线段。
 */
function buildSegmentFromCollectSource(
  source: CollectSource,
  reversed = false,
): ProfileCurveSegment | null {
  const segment = buildLineSegment(source)
    ?? buildArcSegment(source)
    ?? buildEllipseSegment(source)
    ?? buildParabolaSegment(source)
    ?? buildSplineSegment(source);
  if (!segment || !reversed) {
    return segment;
  }
  return reverseProfileCurveSegment(segment);
}

/**
 * 从单个草图对象构建精确曲线段集合。
 *
 * @param object 草图直线/圆弧/椭圆/样条对象。
 * @returns 命中的精确曲线段；不支持时返回空数组。
 */
export function buildProfileCurveSegmentsFromSketchObject(
    object: THREE.Object3D,
): ProfileCurveSegment[] {
    const segment = buildSegmentFromCollectSource(object as unknown as CollectSource);
    return segment ? [segment] : [];
}


/**
 * 反转曲线段方向。
 *
 * 设计说明：
 * 1. 闭合集合的边对象可按反向参与面环，但不应修改草图对象自身定义；
 * 2. Worker 构造 Puppy CAD 内核 wire 时依赖曲线段首尾连续，因此这里输出与面环遍历一致的纯数据副本；
 * 3. 整圆没有起止方向，保持原值即可。
 *
 * @param segment 原始曲线段。
 * @returns 方向反转后的曲线段。
 */
function reverseProfileCurveSegment(segment: ProfileCurveSegment): ProfileCurveSegment {
  switch (segment.kind) {
    case 'line':
      return {
        ...segment,
        start: [...segment.end],
        end: [...segment.start],
      };
    case 'arc':
      return {
        ...segment,
        startAngle: segment.endAngle,
        endAngle: segment.startAngle,
        clockwise: !segment.clockwise,
      };
    case 'spline':
      return {
        ...segment,
        controlPoints: [...segment.controlPoints].reverse().map((point) => [...point] as [number, number, number]),
        curvePoints: [...segment.curvePoints].reverse().map((point) => [...point] as [number, number, number]),
        weights: segment.weights ? [...segment.weights].reverse() : undefined,
      };
    case 'ellipse':
      return {
        ...segment,
        startAngle: segment.endAngle,
        endAngle: segment.startAngle,
        clockwise: !segment.clockwise,
      };
    case 'parabola':
      return {
        ...segment,
        startParameter: segment.endParameter,
        endParameter: segment.startParameter,
        curvePoints: [...segment.curvePoints].reverse().map((point) => [...point] as [number, number, number]),
      };
    case 'circle':
    default:
      return structuredClone(segment);
  }
}

/**
 * 从闭合集合构建与面环方向一致的精确曲线段。
 *
 * 设计说明：
 * 1. `SketchContour` 生成显示填充时已经使用 `SketchClosureResolver` 解析真实闭合遍历；
 * 2. 拉伸精确段必须复用同一套遍历方向，否则线弧混合轮廓会出现显示闭合但 Puppy CAD 内核 wire 断链；
 * 3. 如果拓扑解析失败，则回退到集合原始顺序，避免破坏旧数据的点列兜底能力。
 *
 * @param sources 集合源对象列表。
 * @returns 精确曲线段。
 */
function buildCollectionCurveSegments(sources: readonly CollectSource[]): ProfileCurveSegment[] {
  const firstSource = sources[0];
  const traversalSteps = firstSource
    ? SketchClosureResolver.resolveClosedGeometryTraversal([firstSource], sources)
    : null;
  const buildSteps: ProfileCurveSegmentBuildStep[] = traversalSteps?.length === sources.length
    ? traversalSteps.map((step) => ({
      source: step.source,
      reversed: step.reversed,
    }))
    : sources.map((source) => ({
      source,
      reversed: false,
    }));

  return buildSteps
    .map((step) => buildSegmentFromCollectSource(step.source, step.reversed))
    .filter((segment): segment is ProfileCurveSegment => segment !== null);
}


/**
 * 从轮廓源对象构建外环精确曲线段。
 *
 * @param contour 草图轮廓。
 * @returns 精确曲线段；无法完整表达时返回空数组。
 */
export function buildProfileCurveSegmentsFromSketchContour(
  contour: SketchContour,
): ProfileCurveSegment[] {
  const source = contour.source;
  if (isSketchCircleObject(source)) {
    const axes = resolveReferencePlaneAxes(source);
    return [{
      kind: 'circle',
      center: toPoint3(source.getCenterWorld()),
      radius: source.getRadius(),
      normal: axes.normal,
      xAxis: axes.xAxis,
    }];
  }

  if (isSketchEllipseObject(source)) {
    /*
     * 完整周期判定收口到曲线段工具层：主线程与 worker 必须共用同一套角度容差，
     * 否则“草图判定为整椭圆、构件却按椭圆弧处理”会让拉伸/筋的轮廓边界不一致。
     */
    const ellipseSegment = buildEllipseSegment(source);
    return ellipseSegment && isFullProfileEllipseSegment(ellipseSegment) ? [ellipseSegment] : [];
  }

  if (isSketchSplineObject(source) || isSketchStyleSplineObject(source)) {
    // 自闭合样条的轮廓来源就是样条自身，必须在这里保留曲线语义，避免后续退化为采样折线。
    const splineSegment = buildSplineSegment(source);
    return splineSegment ? [splineSegment] : [];
  }

  if (isSketchCollectionObject(source)) {
    return buildCollectionCurveSegments(Array.from(source.collectSet));
  }

  if (isSketchSlotObject(source)) {
    // 槽口显示点与精确 Puppy CAD 内核 段必须复用同一条共享接点拓扑遍历，避免边顺序变化后 wire 断链。
    return buildCollectionCurveSegments(source.getEdges());
  }

  return [];
}

/**
 * 将外环精确曲线段写入区域几何。
 *
 * @param geometry 区域几何。
 * @param contour 草图轮廓。
 * @returns 带精确曲线段的区域几何。
 */
export function attachProfileCurveSegmentsToRegionGeometry(
  geometry: ProfileRegionGeometry,
  contour: SketchContour,
): ProfileRegionGeometry {
  const outerSegments = buildProfileCurveSegmentsFromSketchContour(contour);
  if (outerSegments.length <= 0) {
    return geometry;
  }
  return {
    ...geometry,
    outerSegments,
  };
}
